import { useQuery } from "@tanstack/solid-query";
import type { TovSourceType } from "contracts/enums/tov";
import { TOV_SOURCE_STATUS, TOV_SOURCE_TYPE } from "contracts/enums/tov";
import { createSignal, For, Show } from "solid-js";
import { isImageMediaType } from "../../boundaries/media-type.protocol";
import { QUERY_ROOT } from "../../enums/shell";
import { api } from "../../store/api";
import { errorMessage } from "../../store/api-client";
import { SOURCE_POLL_MS } from "../../store/pollCadence";
import { queryClient } from "../../store/query";
import { PipelineStepper } from "../../ui/PipelineStepper";
import { ParsedPageViewer } from "./ParsedPageViewer";
import { sourceFailureDetail, sourceStages } from "./sourceStages";
import { isCascadeActive } from "./statusLabels";
import { requestMapping } from "./useMapAttempt";

// Operator source-upload surface: drop a file, watch its status advance
// through the automatic parse -> vectorize -> map cascade, and read the
// parsed pages. Upload attaches to the selected Version; it deliberately does
// not create a new draft Version. Polls only while a source is mid-cascade.
export function SourcesPanel(props: { clientId: string; versionId: string }) {
  const sources = useQuery(() => ({
    queryKey: [QUERY_ROOT.TOV_SOURCES, props.clientId, props.versionId],
    queryFn: () => api.tov.listSources(props.clientId, props.versionId),
    refetchInterval: (current) =>
      isCascadeActive(current.state.data?.sources ?? [])
        ? SOURCE_POLL_MS
        : false,
  }));
  const [selected, setSelected] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");

  const upload = async (file: File) => {
    setError("");
    setBusy(true);
    try {
      await api.tov.uploadSource(props.clientId, props.versionId, file, {
        sourceType: sourceTypeFor(file),
        title: file.name,
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_ROOT.TOV_SOURCES, props.clientId, props.versionId],
      });
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const onPick = (event: { currentTarget: HTMLInputElement }) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (file) {
      void upload(file);
    }
  };

  // Recovery for a failed parse/vectorize cascade; the status leaves `failed`,
  // so the NON_TERMINAL poll picks the source back up automatically.
  const retryParse = async (sourceId: string) => {
    setError("");
    try {
      await api.tov.retryParse(props.clientId, sourceId);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_ROOT.TOV_SOURCES, props.clientId, props.versionId],
      });
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  // A mapping-only failure keeps its parsed pages and embeddings; resuming the
  // version-level map attempt reuses them instead of re-running the cascade.
  const resumeMapping = async () => {
    setError("");
    try {
      await requestMapping(props.clientId, props.versionId);
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  return (
    <section class="tov-group">
      <h2 class="tov-group-title">Sources</h2>
      <label class="tov-upload">
        <input type="file" disabled={busy()} onChange={onPick} />
        <span>{busy() ? "Uploading…" : "Upload a source document"}</span>
      </label>
      <Show when={error()}>
        <p class="gate-error">{error()}</p>
      </Show>
      <div class="tov-evidence-layout">
        <div class="tov-evidence-list">
          <For
            each={sources.data?.sources ?? []}
            fallback={<p class="tov-sub">No sources yet.</p>}
          >
            {(source) => (
              <div class="tov-source-entry">
                <button
                  type="button"
                  class={`tov-source-row ${
                    source.id === selected() ? "is-active" : ""
                  }`}
                  onClick={() => setSelected(source.id)}
                >
                  <span>{source.title}</span>
                </button>
                <PipelineStepper steps={sourceStages(source.status)} compact />
                <Show when={sourceFailureDetail(source)}>
                  {(detail) => (
                    <p class="tov-source-failure" role="alert">
                      <span class="tov-chip danger">
                        Failed: {detail().stage}
                      </span>
                      <span>{detail().message}</span>
                    </p>
                  )}
                </Show>
                <Show when={source.status === TOV_SOURCE_STATUS.FAILED}>
                  <Show
                    when={sourceFailureDetail(source)?.stage === "Parse"}
                    fallback={
                      <button
                        type="button"
                        class="btn"
                        onClick={() => void resumeMapping()}
                      >
                        Resume mapping
                      </button>
                    }
                  >
                    <button
                      type="button"
                      class="btn"
                      onClick={() => void retryParse(source.id)}
                    >
                      Retry parse
                    </button>
                  </Show>
                </Show>
              </div>
            )}
          </For>
        </div>
        <div class="tov-evidence-results">
          <Show
            when={selected()}
            fallback={<p class="tov-sub">Select a source to read its pages.</p>}
          >
            {(sourceId) => (
              <ParsedPageViewer
                clientId={props.clientId}
                sourceId={sourceId()}
              />
            )}
          </Show>
        </div>
      </div>
    </section>
  );
}

// Maps the browser file MIME to a TOV source type. PDFs and images are the
// expected document inputs; anything else is treated as text.
export function sourceTypeFor(file: File): TovSourceType {
  if (file.type === "application/pdf") {
    return TOV_SOURCE_TYPE.PDF;
  }
  if (isImageMediaType(file.type)) {
    return TOV_SOURCE_TYPE.IMAGE;
  }
  return TOV_SOURCE_TYPE.TEXT;
}
